import React, { useState, FC } from 'react'

import { Text, XStack, YStack } from 'tamagui'

import { Grid } from '@newComponents/Grid'
import { Separator } from '@newComponents/Separator'
import { Link, ExternalLink } from '@newComponents/Link'

import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import WindowNew from '@newComponents/Icon/icons/WindowNew'

export const HomeScreenFeaturesCard: FC = () => {
  return (
    <YStack padding="$6" borderRadius="$8" style={{ boxShadow: '0px 100px 80px rgba(85, 51, 235, 0.07), 0px 41.7776px 33.4221px rgba(85, 51, 235, 0.0503198), 0px 22.3363px 17.869px rgba(85, 51, 235, 0.0417275), 0px 12.5216px 10.0172px rgba(85, 51, 235, 0.035), 0px 6.6501px 5.32008px rgba(85, 51, 235, 0.0282725), 0px 2.76726px 2.21381px rgba(85, 51, 235, 0.0196802)' } as any}>
      <YStack gap="$10">
        <YStack $gtXs={{ display: 'none' }}>
          <GuildsAndNetworks />

          <Events />

          <Presentations />
        </YStack>
        <YStack display="none" $gtXs={{ display: 'flex' }}>
          <Grid gap={6}>
            <Grid.Column>
              <GuildsAndNetworks />
            </Grid.Column>

            <Grid.Column>
              <Events />
            </Grid.Column>

            <Grid.Column>
              <Presentations />
            </Grid.Column>
          </Grid>
        </YStack>

        <YStack alignItems='center'>
          <Separator borderBottomColor="$grey6" width={60} />
        </YStack>

        <YStack alignItems='center'>
          <Text fontSize="$4" color="$grey7">
            Not sure about a feature?
          </Text>

          <Text fontSize="$4" color="$grey7" textAlign='center'>
            <ExternalLink href='mailto:hi@guild.host'>
              <Text fontSize="$4" color="$grey7">
                Get in touch
              </Text>
            </ExternalLink>
            , we'd love to chat with you!
          </Text>
        </YStack>
      </YStack>
    </YStack>
  )
}

const GuildsAndNetworks: FC = () => {
  return (
    <YStack gap="$4">
      <Text fontSize="$6" fontWeight="600" fontFamily="$body" color="#5533EB">
        Guilds & Networks
      </Text>

      <YStack gap="$2">
        {[
          <Text fontSize='$4'>Create a Following</Text>,
          <Text fontSize='$4'>Manage your Members</Text>,
          <Text fontSize='$4'>3-tier Permissions System</Text>,
          <Text fontSize='$4'>Inter-connect with Networks</Text>,
          <Text fontSize='$4'>Cross-promote via Networks</Text>,
          <Text fontSize='$4'>Accept Donations</Text>,
          <Text fontSize='$4'>Notify your Slack</Text>,
          <Text fontSize='$4'>Embeddable Widgets</Text>,
          <FeatureLinkBulletItem
            href='/integrations/bluesky'
            label='Announce on Bluesky'
          />,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label='API Access'
            external
          />,
          <FeatureLinkBulletItem
            href='/integrations/meetup'
            label='Mirror your Meetup.com events'
          />,
        ].map((bullet, index) => (
          <XStack gap="$2" key={index}>
            <XStack width={18} minWidth={18} height={18} minHeight={18} backgroundColor="rgba(85, 51, 235, 0.1)" alignItems="center" justifyContent="center" borderRadius={9}>
              <Text color="#5533EB">✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const FeatureLinkBulletItem = ({
  href,
  label,
  external = false,
}: {
  href: string
  label: string
  external?: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const WrappingComponent = external ? ExternalLink : Link

  return (
    <WrappingComponent
      href={href}
      display='flex'
      gap='$2'
      alignItems='center'
      flex={1}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      size='$4'
      animation='medium'
      color='$grey8'
      hoverStyle={{ color: '$darkBlue10', textDecorationLine: 'none' }}
    >
      {label}

      <XStack animation='medium' x={isHovered ? 4 : 0} width='$4' height='$4'>
        <XStack opacity={0.6} position='absolute'>
          <IconWrapper size='$4' color='$grey8'>
            <WindowNew />
          </IconWrapper>
        </XStack>

        <XStack
          animation='medium'
          opacity={isHovered ? 1 : 0}
          position='absolute'
        >
          <IconWrapper size='$4' color='$darkBlue10'>
            <WindowNew />
          </IconWrapper>
        </XStack>
      </XStack>
    </WrappingComponent>
  )
}

const Events: FC = () => {
  return (
    <YStack gap="$4">
      <Text fontSize="$6" fontWeight="600" fontFamily="$body" color="#F26AF4">
        Events
      </Text>

      <YStack gap="$2">
        {[
          <Text fontSize='$4'>In-Person Events</Text>,
          <Text fontSize='$4'>Online Events</Text>,
          <Text fontSize='$4'>Hybrid In-Person & Online Events</Text>,
          <Text fontSize='$4'>Optional Waitlist</Text>,
          <Text fontSize='$4'>Guild Events</Text>,
          <Text fontSize='$4'>One-Off Personal Events</Text>,
          <Text fontSize='$4'>Connect Presentations</Text>,
          <Text fontSize='$4'>Embeddable Widgets</Text>,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label='API Access'
            external
          />,
        ].map((bullet, index) => (
          <XStack gap="$2" key={index}>
            <XStack width={18} minWidth={18} height={18} minHeight={18} backgroundColor="rgba(242, 106, 244, 0.1)" alignItems="center" justifyContent="center" borderRadius={9}>
              <Text color="#F26AF4">✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const Presentations: FC = () => {
  return (
    <YStack gap="$4">
      <Text fontSize="$6" fontWeight="600" fontFamily="$body" color="#F9894B">
        Presentations
      </Text>

      <YStack gap="$2">
        {[
          <Text fontSize='$4'>Manage your Guild's Content Library</Text>,
          <Text fontSize='$4'>Manage your Personal Content Library</Text>,
          <Text fontSize='$4'>Connect Presentations to Events</Text>,
          <Text fontSize='$4'>Connect Presentations to Guilds</Text>,
          <Text fontSize='$4'>Curate Content from top Presenters</Text>,
          <Text fontSize='$4'>Share Content across Networks</Text>,
          <Text fontSize='$4'>Embeddable Widgets</Text>,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label='API Access'
            external
          />,
        ].map((bullet, index) => (
          <XStack gap="$2" key={index}>
            <XStack width={18} minWidth={18} height={18} minHeight={18} backgroundColor="rgba(249, 137, 75, 0.1)" alignItems="center" justifyContent="center" borderRadius={9}>
              <Text color="#F9894B">✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}
